import type { BookingCatalog, BookingLocation, Offer, OfferVariant } from "./booking";

export type Daypart = "any" | "morning" | "afternoon" | "evening";

export type AvailabilityQuery = {
  offerId: string;
  offerVariantId: string;
  bookingLocationId: string;
  from?: string;
  to?: string;
  daypart?: Daypart;
  limit?: number;
};

export type AvailabilityResult = {
  source: "live";
  slots: BookingCatalog["slots"];
  searchedFrom: string;
  searchedTo: string;
  hasMore: boolean;
};

export type ConciergeSelection = {
  offer?: Offer;
  variant?: OfferVariant;
  location?: BookingLocation;
  from?: string;
  to?: string;
  daypart: Daypart;
};

export function validateDateRange(from: string, to: string): string | undefined {
  if (!from || !to) return "Bitte Start- und Enddatum auswählen.";
  if (!isDateKey(from) || !isDateKey(to)) return "Bitte einen gültigen Zeitraum auswählen.";
  if (to < from) return "Das Enddatum muss nach dem Startdatum liegen.";

  const days = differenceInDays(from, to);
  if (days > 92) return "Bitte einen Zeitraum von höchstens 93 Tagen auswählen.";
  return undefined;
}

export function bookingSelectionUrl(baseUrl: string, selection: ConciergeSelection): string {
  const url = new URL(baseUrl);
  const person = url.searchParams.get("person") ?? currentPersonSlug();
  url.search = "";

  if (selection.location?.slug === "google-meet") {
    url.pathname = "/online";
  } else if (selection.location?.slug === "berlin-weissensee") {
    url.pathname = "/berlin";
  } else {
    url.pathname = "/";
    url.searchParams.set("bereich", "termine");
  }

  if (person) url.searchParams.set("person", person);
  if (selection.offer) url.searchParams.set("angebot", selection.offer.slug);
  if (selection.variant) url.searchParams.set("dauer", selection.variant.slug);
  if (selection.location) url.searchParams.set("ort", selection.location.slug);
  if (selection.from) url.searchParams.set("von", selection.from);
  if (selection.to) url.searchParams.set("bis", selection.to);
  if (selection.daypart !== "any") url.searchParams.set("tageszeit", selection.daypart);

  return url.toString();
}

export function daypartLabel(daypart: Daypart): string {
  switch (daypart) {
    case "morning":
      return "Vormittags";
    case "afternoon":
      return "Nachmittags";
    case "evening":
      return "Abends";
    default:
      return "Tageszeit offen";
  }
}

export function parseDaypart(value: string | null): Daypart {
  return value === "morning" || value === "afternoon" || value === "evening" ? value : "any";
}

export function berlinTodayKey(now = new Date()): string {
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Europe/Berlin",
  }).format(now);
}

function currentPersonSlug(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("person");
}

function isDateKey(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T12:00:00Z`));
}

function differenceInDays(from: string, to: string): number {
  const start = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  return Math.round((end - start) / 86_400_000);
}
